import { createContext } from 'react';
import { MFOutlet } from '@im/ui-lib/mf-utils';

export type EnvVars = { [k: string]: string };

const CEP_PREFIX = 'CEP__';
const INGRAM_PREFIX = 'INGRAM__';

type BuildEnvironment =
  | 'development'
  | 'production'
  | 'productionbeta'
  | 'productionbetax'
  | 'staging'
  | 'staging2'
  | 'stagingx'
  | 'test';
const buildEnvironments = [
  'development',
  'production',
  'productionbeta',
  'productionbetax',
  'staging',
  'staging2',
  'stagingx',
  'test',
];

type RequiredKey = 'API_BASE';
type OptionalKey = 'DEV_PROXY_HOSTNAME' | 'API_GCP_BASE' | 'API_EU_GCP_BASE';
const requiredKeys = ['API_BASE'];

type RawBuildConfig = {
  buildEnvironment: BuildEnvironment;
} & { [rk in RequiredKey]: string } & Partial<{
    [ok in OptionalKey]: string;
  }> & { [k: string]: string };

export type BuildConfig = {
  /** @deprecated */
  mfOutlets: MFOutlet[];
  vars: Readonly<RawBuildConfig>;
};

export const buildConfigContext = createContext<BuildConfig | undefined>(
  undefined
);

export function createBuildConfig(
  envVars: EnvVars,
  mfOutlets: MFOutlet[]
): BuildConfig {
  const config: Partial<RawBuildConfig> = {};

  Object.entries(envVars).forEach(([key, value]) => {
    if (key.startsWith(CEP_PREFIX)) {
      config[key.substring(CEP_PREFIX.length)] = value;
    } else if (key.startsWith(INGRAM_PREFIX)) {
      config[key.substring(INGRAM_PREFIX.length)] = value;
    } else if (key === 'NODE_ENV') {
      config.buildEnvironment = value as BuildEnvironment;
    }
  });

  // Validate
  const buildEnvironment = config.buildEnvironment || '';
  if (!buildEnvironments.includes(buildEnvironment)) {
    throw new Error(
      `[BuildConfig] invalid NODE_ENV=${buildEnvironment}; Must be one of: ${JSON.stringify(
        buildEnvironments
      )}.`
    );
  }

  requiredKeys.forEach((key) => {
    if (!config[key]) {
      throw new Error(`[BuildConfig] ${key} not set.`);
    }
  });

  return {
    mfOutlets,
    // Freeze to prevent modifications.
    vars: Object.freeze(config as RawBuildConfig),
  };
}
